import { type Getter, type Setter, atom } from "jotai";
import { atomFamily } from "jotai/utils";

import type {
	HttpResponse,
	QueryError,
	QueryFetchStatus,
	QueryResponse,
	QueryResult,
	QueryStatus,
	QueryTiming,
} from "./request-page.types";

// =============================================================================
// Staleness Calculation
// =============================================================================

/**
 * Determines if query data is stale.
 * Data is stale when we have both data and an error, and the error occurred after the data was fetched.
 */
function isStateStale(state: {
	data: unknown;
	error: unknown;
	dataUpdatedAt: number | undefined;
	errorUpdatedAt: number | undefined;
}): boolean {
	return (
		state.data !== undefined &&
		state.error !== undefined &&
		state.errorUpdatedAt !== undefined &&
		state.dataUpdatedAt !== undefined &&
		state.errorUpdatedAt > state.dataUpdatedAt
	);
}

// =============================================================================
// Query State Types
// =============================================================================

/**
 * Internal state stored per query key.
 * QueryResult is derived from this on read.
 */
export type QueryStateEntry = {
	data: QueryResponse | undefined;
	error: QueryError | undefined;
	fetchStatus: QueryFetchStatus;
	dataUpdatedAt: number | undefined;
	errorUpdatedAt: number | undefined;
	lastAccessedAt: number;
	/** Promise for in-flight request (for deduplication) */
	pendingPromise: Promise<QueryResponse> | undefined;
	/** AbortController for in-flight request (for abort support) */
	abortController: AbortController | undefined;
	/** Timing from last successful fetch */
	timing: QueryTiming | undefined;
};

/**
 * Creates a fresh empty query state entry.
 */
export function createEmptyQueryState(): QueryStateEntry {
	return {
		data: undefined,
		error: undefined,
		fetchStatus: "idle",
		dataUpdatedAt: undefined,
		errorUpdatedAt: undefined,
		lastAccessedAt: Date.now(),
		pendingPromise: undefined,
		abortController: undefined,
		timing: undefined,
	};
}

// =============================================================================
// Query State Keys Tracking
// =============================================================================

/**
 * Tracks all active query state keys.
 * Needed for cleanup iteration since atomFamily doesn't expose its keys.
 */
export const $queryStateKeys = atom(new Set<string>());
$queryStateKeys.debugLabel = "queryStateKeys";

/**
 * Tracks which query state keys belong to which document.
 * Updated by the query executor when it dispatches requests.
 */
export const $queryStateKeysByDocument = atomFamily((documentId: string) => {
	const keysAtom = atom(new Set<string>());
	keysAtom.debugLabel = `queryStateKeysByDocument/${documentId}`;
	keysAtom.debugPrivate = true;
	return keysAtom;
});

/**
 * Tracks which documents currently reference a query state key.
 * Needed so cleanup can prune per-document key sets when a key goes stale.
 */
export const $queryStateKeyOwners = atom(new Map<string, Set<string>>());
$queryStateKeyOwners.debugLabel = "queryStateKeyOwners";
$queryStateKeyOwners.debugPrivate = true;

export function registerQueryStateKeyForDocument(
	get: Getter,
	set: Setter,
	documentId: string,
	key: string
): void {
	const docKeys = get($queryStateKeysByDocument(documentId));
	if (!docKeys.has(key)) {
		set($queryStateKeysByDocument(documentId), new Set([...docKeys, key]));
	}

	const ownersByKey = get($queryStateKeyOwners);
	const owners = ownersByKey.get(key);
	if (owners?.has(documentId)) {
		return;
	}

	const nextOwnersByKey = new Map(ownersByKey);
	const nextOwners = new Set(owners ?? []);
	nextOwners.add(documentId);
	nextOwnersByKey.set(key, nextOwners);
	set($queryStateKeyOwners, nextOwnersByKey);
}

export function removeQueryStateKeyFromDocuments(get: Getter, set: Setter, key: string): void {
	const ownersByKey = get($queryStateKeyOwners);
	const owners = ownersByKey.get(key);
	if (!owners) {
		return;
	}

	for (const documentId of owners) {
		const docKeys = get($queryStateKeysByDocument(documentId));
		if (!docKeys.has(key)) {
			continue;
		}

		const nextDocKeys = new Set(docKeys);
		nextDocKeys.delete(key);
		set($queryStateKeysByDocument(documentId), nextDocKeys);
	}

	const nextOwnersByKey = new Map(ownersByKey);
	nextOwnersByKey.delete(key);
	set($queryStateKeyOwners, nextOwnersByKey);
}

// =============================================================================
// Query State Atom
// =============================================================================

/**
 * Per-key query state storage.
 * Stores data, error, fetch status, and timestamps.
 */
export const $queryState = atomFamily((key: string) => {
	const baseAtom = atom(createEmptyQueryState());
	baseAtom.debugLabel = `queryState/${key}/base`;
	baseAtom.debugPrivate = true;

	const stateAtom = atom(
		(get): QueryStateEntry => get(baseAtom),
		(get, set, update: QueryStateEntry | ((prev: QueryStateEntry) => QueryStateEntry)) => {
			// Ensure key is tracked
			const keys = get($queryStateKeys);
			if (!keys.has(key)) {
				set($queryStateKeys, new Set([...keys, key]));
			}

			const newValue = typeof update === "function" ? update(get(baseAtom)) : update;
			set(baseAtom, newValue);
		}
	);

	stateAtom.debugLabel = `queryState/${key}`;
	return stateAtom;
});

// =============================================================================
// Query Result Derivation
// =============================================================================

/**
 * Derives a full QueryResult from query state.
 * Produces TanStack Query-like result shape.
 *
 * @param state - The current query state entry
 * @param refetch - Function to refetch the query. Required to ensure all QueryResults have a working refetch.
 */
export function deriveQueryResult(
	state: QueryStateEntry,
	refetch: () => Promise<QueryResult>
): QueryResult {
	const { data, error, fetchStatus, dataUpdatedAt, errorUpdatedAt, timing } = state;

	// Determine status based on data and error presence
	let status: QueryStatus;
	if (data !== undefined) {
		status = "success";
	} else if (error !== undefined) {
		status = "error";
	} else {
		status = "pending";
	}

	// Derive booleans
	const isPending = status === "pending";
	const isSuccess = status === "success";
	const isError = status === "error";
	const isFetching = fetchStatus === "fetching";
	const isLoading = isFetching && isPending;
	const isRefetching = isFetching && !isPending;
	const isStale = isStateStale(state);

	return {
		data,
		error,
		status,
		fetchStatus,
		isPending,
		isSuccess,
		isError,
		isLoading,
		isFetching,
		isRefetching,
		isStale,
		dataUpdatedAt,
		errorUpdatedAt,
		timing,
		refetch,
	};
}

// =============================================================================
// Request Page Status (for status badge)
// =============================================================================

export type RequestPageStatus = {
	status: number | string | undefined;
	statusText: string | undefined;
	ok: boolean | undefined;
	isFetching: boolean;
	hasResponse: boolean;
};

export const emptyRequestPageStatus: RequestPageStatus = {
	status: undefined,
	statusText: undefined,
	ok: undefined,
	isFetching: false,
	hasResponse: false,
};

/**
 * Derives RequestPageStatus directly from QueryStateEntry.
 * Used for status badge display in navigation.
 */
export function deriveRequestPageStatus(state: QueryStateEntry): RequestPageStatus {
	const { data, error, fetchStatus } = state;
	const isStale = isStateStale(state);

	// For non-HTTP responses (Postgres, JavaScript, gRPC), show "OK" or gRPC status name
	// instead of a numeric HTTP status code.
	const dataStatus =
		data === undefined
			? undefined
			: data.kind === "postgres" || data.kind === "sqlite" || data.kind === "javascript"
				? "OK"
				: data.kind === "grpc"
					? data.statusCode === 0
						? "OK"
						: "ERR"
					: data.status;

	// If stale (error after success), prefer error status; otherwise prefer data status.
	// For errors without an HTTP status code (e.g. Postgres/JS/gRPC errors), show "ERR".
	const errorStatus = error ? (error.status ?? "ERR") : undefined;
	const status = isStale ? (errorStatus ?? dataStatus) : (dataStatus ?? errorStatus);
	const hasResponse = status !== undefined;

	// ok is only true if we have data and it's not stale
	const ok =
		data !== undefined &&
		!isStale &&
		(data.kind === "postgres" || data.kind === "sqlite" || data.kind === "javascript"
			? true
			: data.kind === "grpc"
				? data.statusCode === 0
				: data.ok === true);

	const statusText =
		isStale || !data
			? undefined
			: data.kind === "postgres" || data.kind === "sqlite"
				? `${data.rowCount} row${data.rowCount === 1 ? "" : "s"}`
				: data.kind === "javascript"
					? undefined
					: data.kind === "grpc"
						? `${data.messages.length} msg${data.messages.length === 1 ? "" : "s"}`
						: data.statusText;

	return {
		status,
		statusText,
		ok,
		isFetching: fetchStatus === "fetching",
		hasResponse,
	};
}

// =============================================================================
// Error Classification
// =============================================================================

/**
 * Maps raw error messages to structured QueryError with typed codes.
 */
export function classifyError(error: {
	message: string;
	status?: number;
	responseBody?: string;
	authHint?: { provider: string; cluster_name?: string; region?: string; account_id?: string };
}): QueryError {
	const message = error.message.toLowerCase();

	// AWS EKS login required (more specific than generic AUTH_ERROR)
	if (error.status === 401 && error.authHint?.provider === "aws-eks") {
		const parts: string[] = ["This EKS cluster requires AWS credentials."];
		if (error.authHint.cluster_name) {
			parts.unshift(`Cluster: ${error.authHint.cluster_name}.`);
		}
		if (error.authHint.region) {
			parts.unshift(`Region: ${error.authHint.region}.`);
		}
		return {
			code: "AWS_LOGIN_REQUIRED",
			message: parts.join(" "),
			status: error.status,
			responseBody: error.responseBody,
			authHintDetails: {
				cluster_name: error.authHint.cluster_name,
				region: error.authHint.region,
				account_id: error.authHint.account_id,
			},
		};
	}

	// AWS login required (no profile selected or expired/invalid credentials)
	if (
		message.includes("no aws account/role selected") ||
		message.includes("failed to load credentials") ||
		message.includes("no aws login session is active") ||
		message.includes("security token included in the request is invalid")
	) {
		return {
			code: "AWS_LOGIN_REQUIRED",
			message: error.message,
		};
	}

	// HTTP status-based errors
	if (error.status === 401 || error.status === 403) {
		return {
			code: "AUTH_ERROR",
			message: error.message,
			status: error.status,
			responseBody: error.responseBody,
		};
	}

	// Variable resolution errors
	if (message.includes("unresolved variable")) {
		return { code: "UNRESOLVED_VARS", message: error.message };
	}

	// Page lookup errors
	if (message.includes("request page") && message.includes("not found")) {
		return { code: "PAGE_NOT_FOUND", message: error.message };
	}
	if (message.includes("request page") && message.includes("not loaded")) {
		return { code: "PAGE_NOT_LOADED", message: error.message };
	}
	if (message.includes("metadata not loaded")) {
		return { code: "PAGE_NOT_LOADED", message: error.message };
	}

	// Timeout errors
	if (message.includes("timed out") || message.includes("request timed out")) {
		return { code: "TIMEOUT", message: error.message };
	}

	// Network errors
	if (
		message.includes("network") ||
		message.includes("failed to fetch") ||
		message.includes("connection") ||
		message.includes("dns") ||
		message.includes("econnrefused") ||
		message.includes("enotfound")
	) {
		return { code: "NETWORK_ERROR", message: error.message };
	}

	return {
		code: "UNKNOWN",
		message: error.message,
		status: error.status,
		responseBody: error.responseBody,
	};
}
